import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Search, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import TreeCalculator from './TreeCalculator';
import CultivarPicker, { type PickedCultivar } from './CultivarPicker';
import orchardService from '../../services/orchard';
import cultivarService from '../../services/cultivar';
import { TRAINING_SYSTEMS } from '../../types/orchard';
import PbrMark from '../cultivar/PbrMark';

const schema = z.object({
  name: z.string().min(1, 'Area name is required').max(255),
  // Required unless the farm plans by position, where an area of many
  // cultivars may have no main one (checked in submitWithSupply).
  cultivar_id: z.number({ message: 'Select a cultivar' }).int().positive().nullable().optional(),
  area_hectares: z.number().positive().optional(),
  tree_count: z.number().int().min(0).optional(),
  row_spacing_meters: z.number().positive().optional(),
  tree_spacing_meters: z.number().positive().optional(),
  planting_date: z.string().optional(),
  // Composite tree. The *_cultivar_id fields are the real references; the
  // free-text `rootstock` is kept in sync for blocks whose stock never makes
  // it into the registry.
  rootstock: z.string().max(255).optional(),
  rootstock_cultivar_id: z.number().int().positive().nullable().optional(),
  interstem_cultivar_id: z.number().int().positive().nullable().optional(),
  graft_union_height_mm: z.number().int().min(0).max(2000).nullable().optional(),
  irrigation_zone: z.string().max(100).optional(),
  training_system: z.string().optional(),
  tree_supplier: z.string().max(255).optional(),
  // The supplier as an organisation. `tree_supplier` keeps the name for a
  // nursery that is not on the platform — a block planted in 1998 may have
  // come from one that never will be.
  supplied_by_nursery_org_id: z.number().int().positive().nullable().optional(),
  // Set only when mapping trees that are already in the ground with no
  // planting licence covering them. Declaring it is what lets the area be
  // created at all; the server refuses otherwise rather than inventing a
  // licence nobody granted.
  pre_existing_supply: z
    .object({
      nursery_org_id: z.number().int().positive().nullable().optional(),
      supplier_name: z.string().max(255).optional(),
      planting_year: z.number().int().min(1900).max(2200).nullable().optional(),
    })
    .nullable()
    .optional(),
  notes: z.string().optional(),
  active: z.boolean().optional(),
});

type AreaFieldData = z.infer<typeof schema>;

interface CultivarAreaFormProps {
  initialValues?: Partial<AreaFieldData>;
  /** Registry names for a rootstock/interstem already linked, so edit mode
   *  shows what was chosen rather than an empty search box. */
  initialRootstockName?: string | null;
  initialInterstemName?: string | null;
  areaFromMap?: number | null;
  onSubmit: (data: AreaFieldData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  hasPolygon?: boolean;
  /** 'rows': a planting plan below the form owns spacing and tree count, so
   *  the spacing estimate is hidden rather than left to disagree with it. */
  plantedBy?: 'estimate' | 'rows' | 'positions';
  /** Trees the rows hold, shown in place of the estimate. */
  rowTreeCount?: number | null;
  /** The main cultivar as it is picked, for the plan's pattern and summary. */
  onCultivarChange?: (id: number | null, displayName: string | null) => void;
}

function CultivarAreaForm({
  initialValues,
  initialRootstockName,
  initialInterstemName,
  areaFromMap,
  onSubmit,
  onCancel,
  isSubmitting = false,
  hasPolygon = false,
  plantedBy = 'estimate',
  rowTreeCount = null,
  onCultivarChange,
}: CultivarAreaFormProps) {
  const byRows = plantedBy === 'rows';
  // The farm plans by row and position: what is planted is set on the area's
  // page, and a planting of many cultivars needs no main one.
  const byPositions = plantedBy === 'positions';
  const [cultivarSearch, setCultivarSearch] = useState('');
  type CultivarOption = { id: number; name: string; trade_name: string | null; crop_type_name: string | null; protection_status?: string | null };
  const [cultivarResults, setCultivarResults] = useState<CultivarOption[]>([]);
  const [selectedCultivar, setSelectedCultivar] = useState<CultivarOption | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Nursery selection state
  type NurseryOption = { id: number; nursery_org_id: number; nursery_org_name: string; availability_status: string };
  const [nurserySearch, setNurserySearch] = useState('');
  const [nurseries, setNurseries] = useState<NurseryOption[]>([]);
  const [selectedNursery, setSelectedNursery] = useState<NurseryOption | null>(null);
  const [nurseryLoading, setNurseryLoading] = useState(false);
  const [showNurseryDropdown, setShowNurseryDropdown] = useState(false);

  // Mapping trees that are already in the ground. There is no planting licence
  // behind them and there never will be — whatever covered them was agreed on
  // paper years ago. Saying so is what lets the block be mapped; the server
  // refuses a licence-less area that has not declared it.
  const [preExisting, setPreExisting] = useState(false);
  const [preExistingSupplierName, setPreExistingSupplierName] = useState('');
  const [preExistingYear, setPreExistingYear] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
  } = useForm<AreaFieldData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initialValues?.name || '',
      cultivar_id: initialValues?.cultivar_id,
      area_hectares: initialValues?.area_hectares,
      tree_count: initialValues?.tree_count,
      row_spacing_meters: initialValues?.row_spacing_meters,
      tree_spacing_meters: initialValues?.tree_spacing_meters,
      planting_date: initialValues?.planting_date || '',
      rootstock: initialValues?.rootstock || '',
      rootstock_cultivar_id: initialValues?.rootstock_cultivar_id ?? null,
      interstem_cultivar_id: initialValues?.interstem_cultivar_id ?? null,
      graft_union_height_mm: initialValues?.graft_union_height_mm ?? null,
      irrigation_zone: initialValues?.irrigation_zone || '',
      training_system: initialValues?.training_system || '',
      tree_supplier: initialValues?.tree_supplier || '',
      notes: initialValues?.notes || '',
      active: initialValues?.active !== false,
    },
  });

  const areaHa = watch('area_hectares');
  const rowSpacing = watch('row_spacing_meters');
  const treeSpacing = watch('tree_spacing_meters');
  const treeCount = watch('tree_count');

  // Open the composite-tree section on load only when the area already has
  // something in it — otherwise it is noise for the ungrafted majority.
  const hasCompositeDetail = Boolean(
    initialValues?.interstem_cultivar_id || initialValues?.graft_union_height_mm
  );

  // Sync area from map polygon
  useEffect(() => {
    if (areaFromMap) {
      setValue('area_hectares', areaFromMap);
    }
  }, [areaFromMap, setValue]);

  // Auto-calculate tree count when spacing changes. Not when planted by rows:
  // the rows count the trees.
  useEffect(() => {
    const area = areaHa ?? areaFromMap;
    if (!byRows && area && rowSpacing && treeSpacing) {
      const count = orchardService.calculateTreeCount(area, rowSpacing, treeSpacing);
      setValue('tree_count', count);
    }
  }, [byRows, areaHa, areaFromMap, rowSpacing, treeSpacing, setValue]);

  // Search cultivars
  const searchCultivars = useCallback(async (query: string) => {
    if (query.length < 1) {
      setCultivarResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await orchardService.searchCultivars(query, 10);
      if (response.success && response.data) {
        setCultivarResults(response.data.cultivars);
      }
    } catch {
      setCultivarResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchCultivars(cultivarSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [cultivarSearch, searchCultivars]);

  // Fetch nurseries for selected cultivar
  const fetchNurseriesForCultivar = useCallback(async (cultivarId: number) => {
    setNurseryLoading(true);
    try {
      const response = await cultivarService.getNurseriesForCultivar(cultivarId);
      if (response.success && response.data) {
        setNurseries(response.data as NurseryOption[]);
        setNurserySearch('');
        setSelectedNursery(null);
      } else {
        setNurseries([]);
      }
    } catch {
      setNurseries([]);
    } finally {
      setNurseryLoading(false);
    }
  }, []);

  const handleSelectCultivar = (cultivar: CultivarOption) => {
    setSelectedCultivar(cultivar);
    setValue('cultivar_id', cultivar.id, { shouldValidate: true });
    const display = cultivar.trade_name ? `${cultivar.name} (${cultivar.trade_name})` : cultivar.name;
    setCultivarSearch(display);
    setShowDropdown(false);
    onCultivarChange?.(cultivar.id, display);
    // Fetch nurseries for this cultivar
    fetchNurseriesForCultivar(cultivar.id);
  };

  const handleSelectNursery = (nursery: NurseryOption) => {
    setSelectedNursery(nursery);
    // The name AND the organisation. The name alone cannot be joined, counted
    // or asked to confirm what it supplied — which is the whole point of
    // recording a supplier on a block nobody licensed.
    setValue('tree_supplier', nursery.nursery_org_name, { shouldValidate: true });
    setValue('supplied_by_nursery_org_id', nursery.nursery_org_id ?? null, { shouldValidate: true });
    setNurserySearch(nursery.nursery_org_name);
    setShowNurseryDropdown(false);
  };

  // Attach the pre-existing declaration, if one was made. Kept out of the form
  // state proper because it describes how this area came to be recorded, not
  // anything about the area itself.
  const submitWithSupply = (data: AreaFieldData) => {
    // A mixed block has no main cultivar: what grows is its positions.
    if (byPositions) data = { ...data, cultivar_id: null };
    if (!byPositions && !data.cultivar_id) {
      setError('cultivar_id', { message: 'Select a cultivar' });
      return;
    }
    if (!preExisting) {
      onSubmit({ ...data, pre_existing_supply: null });
      return;
    }
    onSubmit({
      ...data,
      pre_existing_supply: {
        nursery_org_id: data.supplied_by_nursery_org_id ?? null,
        supplier_name: preExistingSupplierName.trim() || data.tree_supplier || '',
        planting_year: preExistingYear ? Number(preExistingYear) : null,
      },
    });
  };

  // The free-text `rootstock` mirrors the chosen registry name so existing
  // readers (declarations, exports, legacy blocks) keep working unchanged.
  const handleRootstockChange = (c: PickedCultivar | null) => {
    setValue('rootstock_cultivar_id', c ? c.id : null, { shouldValidate: true });
    setValue('rootstock', c ? c.name : '', { shouldValidate: true });
  };

  const handleInterstemChange = (c: PickedCultivar | null) => {
    setValue('interstem_cultivar_id', c ? c.id : null, { shouldValidate: true });
  };

  return (
    <form onSubmit={handleSubmit(submitWithSupply)} className="space-y-4">
      {/* Area Name */}
      <Input
        label="Area Name *"
        placeholder="e.g., Row 1-20, Red Delicious Block"
        error={errors.name?.message}
        {...register('name')}
      />

      {/* Cultivar Selector. A mixed block has none: its rows say what grows. */}
      {byPositions ? (
        <div className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-900">
          <p className="font-semibold">Mixed block</p>
          <p className="text-xs mt-1">
            This farm plans plantings by rows and positions. The cultivars are the ones planted in its rows, set
            under Rows and positions, and the tree count is their total. Spraying, jobs, harvest and reports read
            them from there.
          </p>
        </div>
      ) : (
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-neutral-700">
          {byRows ? 'Main cultivar *' : 'Cultivar / Variety *'}
        </label>
        {byRows && (
          <p className="text-xs text-neutral-500">
            Every row is this cultivar unless the row pattern below says otherwise. It is also
            the cultivar the planting licence is checked against.
          </p>
        )}
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              className="input-professional w-full pl-9"
              placeholder="Search cultivar registry..."
              value={cultivarSearch}
              onChange={(e) => {
                setCultivarSearch(e.target.value);
                setShowDropdown(true);
                if (!e.target.value) {
                  setSelectedCultivar(null);
                  setValue('cultivar_id', undefined as any);
                  onCultivarChange?.(null, null);
                }
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            />
            {searchLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-neutral-400" />
            )}
          </div>

          {showDropdown && cultivarResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {cultivarResults.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="w-full text-left px-4 py-2.5 hover:bg-primary-50 focus:bg-primary-50 text-sm border-b border-neutral-100 last:border-0"
                  onClick={() => handleSelectCultivar(c)}
                >
                  <div className="font-medium text-neutral-900">
                    {c.name}
                    <PbrMark status={c.protection_status} />
                  </div>
                  {c.trade_name && (
                    <div className="text-xs text-neutral-500">{c.trade_name}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        {selectedCultivar && (
          <p className="text-xs text-success-600">
            ✓ Selected: {selectedCultivar.name}
            <PbrMark status={selectedCultivar.protection_status} />
            {selectedCultivar.crop_type_name && ` · ${selectedCultivar.crop_type_name}`}
          </p>
        )}
        {errors.cultivar_id && (
          <p className="text-xs text-error-600">{errors.cultivar_id.message}</p>
        )}
      </div>
      )}

      {byRows ? (
        <div className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-900">
          {rowTreeCount
            ? <><strong>{rowTreeCount.toLocaleString()}</strong> trees, counted from the rows below.</>
            : 'Tree count and spacing come from the rows below.'}
        </div>
      ) : (<>
      {/* Spacing inputs */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-neutral-700">Row Spacing (m)</label>
          <input
            type="number"
            step="0.1"
            min="0.1"
            className="input-professional w-full"
            placeholder="e.g., 4.5"
            {...register('row_spacing_meters', { valueAsNumber: true })}
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-neutral-700">Tree Spacing (m)</label>
          <input
            type="number"
            step="0.1"
            min="0.1"
            className="input-professional w-full"
            placeholder="e.g., 2.0"
            {...register('tree_spacing_meters', { valueAsNumber: true })}
          />
        </div>
      </div>

      {/* Tree Count Calculator */}
      <TreeCalculator
        areaHectares={areaHa ?? areaFromMap}
        rowSpacingMeters={rowSpacing}
        treeSpacingMeters={treeSpacing}
        estimatedCount={treeCount}
      />

      {/* Tree Count (manual override) */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-neutral-700">Tree Count</label>
        <input
          type="number"
          step="1"
          min="0"
          className="input-professional w-full"
          placeholder="Auto-calculated or enter manually"
          {...register('tree_count', { valueAsNumber: true })}
        />
        <p className="text-xs text-neutral-500">Auto-calculated from spacing — override if needed</p>
      </div>
      </>)}

      {/* Area (read-only if from polygon) */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-neutral-700">Area (hectares)</label>
        <input
          type="number"
          step="0.01"
          className="input-professional w-full bg-neutral-50"
          placeholder="Auto-calculated from polygon"
          {...register('area_hectares', { valueAsNumber: true })}
        />
        {areaFromMap && (
          <p className="text-xs text-success-600">✓ Calculated from drawn polygon</p>
        )}
      </div>

      {/* Additional fields */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-neutral-700">Planting Date</label>
        <input
          type="date"
          className="input-professional w-full"
          {...register('planting_date')}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-neutral-700">Training System</label>
        <select className="input-professional w-full" {...register('training_system')}>
          <option value="">Select training system...</option>
          {TRAINING_SYSTEMS.map((ts) => (
            <option key={ts} value={ts}>{ts}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <CultivarPicker
          label="Rootstock"
          placeholder="Search rootstock cultivars..."
          cultivarRole="rootstock"
          value={watch('rootstock_cultivar_id')}
          initialDisplayName={initialRootstockName || initialValues?.rootstock || ''}
          onChange={handleRootstockChange}
          error={errors.rootstock_cultivar_id?.message}
        />
        <Input
          label="Irrigation Zone"
          placeholder="e.g., Zone A, Valve 3"
          {...register('irrigation_zone')}
        />
      </div>

      {/* Interstem and graft union — only relevant to grafted (composite)
          trees, so kept behind a disclosure rather than in everyone's way. */}
      <details className="rounded-lg border border-neutral-200 p-3" open={hasCompositeDetail}>
        <summary className="cursor-pointer text-sm font-semibold text-neutral-700">
          Interstem &amp; graft union
        </summary>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <CultivarPicker
            label="Interstem"
            hint="Only where an intermediate stem-piece is used. May be a scion or a rootstock cultivar."
            placeholder="Search cultivars..."
            value={watch('interstem_cultivar_id')}
            initialDisplayName={initialInterstemName || ''}
            onChange={handleInterstemChange}
            error={errors.interstem_cultivar_id?.message}
          />
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-neutral-700">
              Graft union height (mm)
            </label>
            <input
              type="number"
              step="1"
              min="0"
              className="input-professional w-full"
              placeholder="e.g., 100"
              {...register('graft_union_height_mm', {
                setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
              })}
            />
            <p className="text-xs text-neutral-500">
              Height above finished soil level. A buried union lets the scion root on its own.
            </p>
            {errors.graft_union_height_mm && (
              <p className="text-xs text-error-600">{errors.graft_union_height_mm.message}</p>
            )}
          </div>
        </div>
      </details>

      {/* Nursery/Tree Supplier - Only show if cultivar is selected */}
      {selectedCultivar && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-neutral-700">
            Tree Supplier / Nursery *
          </label>
          {nurseries.length > 0 ? (
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input
                  className="input-professional w-full pl-9"
                  placeholder="Search available nurseries..."
                  value={nurserySearch}
                  onChange={(e) => {
                    setNurserySearch(e.target.value);
                    setShowNurseryDropdown(true);
                    if (!e.target.value) {
                      setSelectedNursery(null);
                      setValue('tree_supplier', undefined as any);
                    }
                  }}
                  onFocus={() => setShowNurseryDropdown(true)}
                  onBlur={() => setTimeout(() => setShowNurseryDropdown(false), 200)}
                />
                {nurseryLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-neutral-400" />
                )}
              </div>

              {showNurseryDropdown && nurseries.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {nurseries
                    .filter((n) =>
                      n.nursery_org_name.toLowerCase().includes(nurserySearch.toLowerCase())
                    )
                    .map((nursery) => (
                      <button
                        key={`${nursery.nursery_org_id}-${nursery.id}`}
                        type="button"
                        className="w-full text-left px-4 py-2.5 hover:bg-primary-50 focus:bg-primary-50 text-sm border-b border-neutral-100 last:border-0"
                        onClick={() => handleSelectNursery(nursery)}
                      >
                        <div className="font-medium text-neutral-900">{nursery.nursery_org_name}</div>
                        <div className="text-xs text-neutral-500">
                          Status: {nursery.availability_status}
                        </div>
                      </button>
                    ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              No nurseries found supplying this cultivar
            </div>
          )}
          {selectedNursery && (
            <p className="text-xs text-success-600">
              ✓ Selected: {selectedNursery.nursery_org_name}
            </p>
          )}

          {/* Mapping a block that is already planted.
              These trees went in under an arrangement made before Factree, or
              off the platform entirely. There is no licence to find and the
              area would otherwise be refused outright — so the orchard says so,
              and names who grew the trees. That name is the only thing that
              makes a retrospective block checkable by anyone: the nursery can
              be asked to confirm the count and the year. */}
          <div className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3 space-y-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                checked={preExisting}
                onChange={(e) => setPreExisting(e.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-neutral-800">
                  These trees are already planted
                </span>
                <span className="block text-xs text-neutral-500">
                  Tick this when mapping an existing block that no planting licence covers.
                  It records the planting so the block can be used; it does not create an
                  agreement, and nothing here changes whatever arrangement the trees went
                  in under.
                </span>
              </span>
            </label>

            {preExisting && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-6">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-neutral-700">
                    Supplying nursery
                  </label>
                  <input
                    className="input-professional w-full text-sm"
                    placeholder={selectedNursery ? selectedNursery.nursery_org_name : 'Nursery name'}
                    value={preExistingSupplierName}
                    onChange={(e) => setPreExistingSupplierName(e.target.value)}
                  />
                  <p className="text-xs text-neutral-500">
                    {selectedNursery
                      ? 'Using the nursery selected above unless you type a different one.'
                      : 'Type the name if the nursery is not on Factree.'}
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-neutral-700">
                    Year planted
                  </label>
                  <input
                    className="input-professional w-full text-sm"
                    type="number"
                    placeholder="e.g. 2019"
                    value={preExistingYear}
                    onChange={(e) => setPreExistingYear(e.target.value)}
                  />
                  <p className="text-xs text-neutral-500">
                    The nursery can be asked to confirm this.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-neutral-700">Notes</label>
        <textarea
          className="input-professional w-full resize-none"
          rows={3}
          placeholder="Any additional notes..."
          {...register('notes')}
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="active"
          className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          {...register('active')}
          defaultChecked
        />
        <label htmlFor="active" className="text-sm text-neutral-700">Active area</label>
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          type="submit"
          variant="primary"
          isLoading={isSubmitting}
          disabled={!hasPolygon}
        >
          Save Cultivar Area
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {!hasPolygon && (
        <p className="text-xs text-amber-600">
          Draw an area boundary on the map before saving
        </p>
      )}
    </form>
  );
}

export default CultivarAreaForm;
